import { keyframes, Palette, useTheme } from "@mui/material";
import { borderGeneralStyle, generalStyles } from "@utils/cssStyles";

interface IUseAskDostStylesProps {
  isFullWidth?: boolean;
}

export const getCategoryPillStyle = (palette: Palette, isActive: boolean) => {
  const activeBg =
    palette.mode === "dark" ? palette.common.white : palette.text.heading;
  const activeColor =
    palette.mode === "dark" ? palette.text.heading : palette.common.white;

  return {
    px: 1.5,
    py: 0.5,
    borderRadius: 999,
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap" as const,
    backgroundColor: isActive ? activeBg : "transparent",
    color: isActive ? activeColor : palette.text.secondary,
    "&:hover": {
      backgroundColor: isActive ? activeBg : palette.text.input,
    },
  };
};

export const getConversationRowStyle = (
  palette: Palette,
  isSelected: boolean,
  size: { px: number; borderRadius: number } = { px: 1, borderRadius: 1 },
) => ({
  ...generalStyles.flexBetweenStyle,
  gap: 1.5,
  px: size.px,
  py: 1,
  borderRadius: size.borderRadius,
  cursor: "pointer",
  backgroundColor: isSelected ? palette.action.selected : "transparent",
  "&:hover": { backgroundColor: palette.text.input },
  "&:hover .askdost-history-action-btn": { opacity: 1 },
});

export const conversationRowTitleStyle = (
  palette: Palette,
  isSelected: boolean,
) => ({
  color: isSelected ? palette.text.heading : palette.text.primary,
  fontWeight: isSelected ? 600 : 400,
  fontSize: "14px",
});

export const getActionRowIconButtonStyle = (
  palette: Palette,
  isMenuOpen: boolean,
) => ({
  flexShrink: 0,
  opacity: isMenuOpen ? 1 : 0,
  transition: "opacity 0.15s ease",
  color: palette.text.disabled,
  "&:hover": { color: palette.text.heading, backgroundColor: "transparent" },
});

export const conversationActionMenuPopoverStyle = { width: 180, p: 1 };

export const getConversationActionMenuItemStyle = (
  palette: Palette,
  tone: "default" | "danger" = "default",
) => ({
  gap: 2,
  px: 2,
  py: 1,
  borderRadius: 1,
  fontSize: "14px",
  color: tone === "danger" ? palette.error.main : palette.text.primary,
  "&:hover": {
    backgroundColor: tone === "danger" ? palette.error.lighter : palette.text.input,
  },
});

export const renameModalTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    overflow: "visible", 
    "& fieldset": {
      top: 0, 
    },
    "& legend": {
      display: "none", 
    },
  },
};

export const renameModalActionsStyle = { justifyContent: "flex-end" };

export const historySearchFieldStyle = (borderRadius: string) => ({
  "& .MuiOutlinedInput-root": { borderRadius },
  "& .MuiOutlinedInput-notchedOutline": { top: 0 },
  "& legend": { display: "none" },
});

export const historyEmptyStateStyle = (palette: Palette) => ({
  color: palette.text.label,
  fontSize: "12px",
});

export const getPillTextButtonStyle = (palette: Palette) => ({
  px: 1.5,
  py: 0.5,
  borderRadius: 1,
  border: `1px solid ${palette.primary.main}`,
  fontSize: "12px",
  fontWeight: 500,
  color: palette.primary.main,
  cursor: "pointer",
  whiteSpace: "nowrap" as const,
  "&:hover": { backgroundColor: palette.text.input },
});

export const getPillIconButtonStyle = (
  palette: Palette,
  tone: "primary" | "neutral" = "primary",
) => ({
  height: 28,
  px: 1.5,
  borderRadius: 1,
  border: `1px solid ${
    tone === "primary" ? palette.primary.main : palette.text.borderLine
  }`,
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: "12px",
  fontWeight: 500,
  color: tone === "primary" ? palette.primary.main : palette.text.label,
  cursor: "pointer",
  whiteSpace: "nowrap" as const,
  "&:hover":
    tone === "primary"
      ? { backgroundColor: palette.text.input }
      : { borderColor: palette.error.main, color: palette.error.main },
});

export const getIconBadgeStyle = (
  palette: Palette,
  options: { size?: number; variant?: "bordered" | "filled" } = {},
) => {
  const { size = 36, variant = "bordered" } = options;
  return {
    flexShrink: 0,
    width: size,
    height: size,
    borderRadius: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    ...(variant === "bordered"
      ? { border: `1px solid ${palette.text.borderLine}` }
      : { backgroundColor: palette.text.input }),
  };
};

export const askDostShadowStyle = {
  position: "absolute",
  top: "-60px",
  left: "-5%",
  right: "-5%",
  bottom: "-20px",
  pointerEvents: "none",
  zIndex: 0,
  background:
    "radial-gradient(ellipse at 50% 80%, rgba(0, 185, 255, 0.35) 0%, rgba(29, 95, 224, 0.15) 50%, transparent 70%)",
  filter: "blur(35px)",
};

export const askDostRotatingBorderStyle = {
  position: "relative",
  zIndex: 1,
  borderRadius: "32px",
  padding: "2px",
  width: "100%",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-150%",
    left: "-40%",
    width: "180%",
    height: "400%",
    background:
      "conic-gradient(from 0deg, rgba(0,185,255,0) 0deg, rgba(0,185,255,0.8) 40deg, rgba(29,95,224,0.4) 80deg, rgba(0,185,255,0) 140deg, rgba(0,185,255,0) 220deg, rgba(0,185,255,0.6) 285deg, rgba(0,185,255,0) 340deg)",
    animation: "dostOrbit 5.5s linear infinite",
    zIndex: 0,
  },
  "@keyframes dostOrbit": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

export const getAskDostInnerBoxStyle = (
  isDragActive: boolean,
  palette: Palette,
) => ({
  position: "relative",
  zIndex: 1,
  borderRadius: "30px",
  background: isDragActive
    ? palette.mode === "dark"
      ? palette.grey[800]
      : "#F4FAFF"
    : palette.mode === "dark"
    ? palette.grey[900]
    : "#FFFFFF",
  p: "12px 16px",
  width: "100%",
  transition: "background-color 0.2s ease-in-out",
  border:
    palette.mode === "dark"
      ? `1px solid ${palette.divider}`
      : "1px solid #E0E0E0",
});

export const useAskDostStyles = (props: IUseAskDostStylesProps) => {
  const { isFullWidth } = props;
  const { palette } = useTheme();

  return {
    askDostButton: {
      borderRadius: 1,
      border: "none",
      textTransform: "none",
      background: `radial-gradient(circle, rgba(232,246,255,0.35) 0%, rgba(232,246,255,0.35) 100%)`,
      animation: "rotateGlow 4s linear infinite",
      "@keyframes rotateGlow": {
        "0%": { boxShadow: "inset -14px 11px 12px rgba(29, 95, 224, 0.25)" },
        "12.5%": { boxShadow: "inset -14px 0px 12px rgba(29, 95, 224, 0.25)" },
        "25%": { boxShadow: "inset -14px -11px 12px rgba(29, 95, 224, 0.25)" },
        "37.5%": { boxShadow: "inset 0px -11px 12px rgba(29, 95, 224, 0.25)" },
        "50%": { boxShadow: "inset 14px -11px 12px rgba(29, 95, 224, 0.25)" },
        "62.5%": { boxShadow: "inset 14px 0px 12px rgba(29, 95, 224, 0.25)" },
        "75%": { boxShadow: "inset 14px 11px 12px rgba(29, 95, 224, 0.25)" },
        "87.5%": { boxShadow: "inset 0px 11px 12px rgba(29, 95, 224, 0.25)" },
        "100%": { boxShadow: "inset -14px 11px 12px rgba(29, 95, 224, 0.25)" },
      },
      "&:hover": {
        background: `radial-gradient(circle, rgba(232,246,255,0.45) 0%, rgba(232,246,255,0.45) 100%)`,
        border: "none",
      },
    },

    askDostDrawer: {
      pointerEvents: "none",
      "& .MuiDrawer-paper": {
        pointerEvents: "auto",
        position: "fixed",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition:
          "width 0.3s ease, top 0.3s ease, right 0.3s ease, bottom 0.3s ease, left 0.3s ease, border-radius 0.3s ease",
        ...(isFullWidth
          ? {
              top: "0 !important",
              right: "0 !important",
              bottom: "0 !important",
              left: "auto",
              width: "100%",
              maxWidth: "100%",
              height: "100% !important",
              borderRadius: "0 !important",
              boxShadow: "none !important",
              border: "none",
            }
          : {
              top: "18px !important",
              right: "18px !important",
              bottom: "18px !important",
              left: "auto",
              height: "auto !important",
              width: {
                xs: "calc(100% - 36px)",
                sm: "calc(100% - 36px)",
                md: 600,
                lg: 520,
              },
              maxWidth: "calc(100% - 36px)",
              borderRadius: "22px !important",
              boxShadow: "0 24px 60px rgba(0,29,63,0.18) !important",
              border: `1px solid ${palette.text.borderLine}`,
            }),
      },
    },
  };
};

export const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

export const greetingContainerStyle = {
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  mb: 4,
};

export const getGreetingIconBoxStyle = (palette: Palette) => ({
  color: palette.primary.main,
  mb: 1.5,
});

export const greetingTitleStyle = {
  fontWeight: 700,
  mb: 1,
  letterSpacing: "-0.5px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "1.3em",
  background:
    "linear-gradient(100deg, #001D3F 0%, #003F83 32%, #1D5FE0 62%, #00B9FF 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};

export const getGreetingCaretStyle = (palette: Palette) => ({
  display: "inline-block",
  width: "4px",
  height: "1.1em",
  verticalAlign: "text-bottom",
  ml: "4px",
  backgroundColor: palette.info.main,
  animation: `${caretBlink} 900ms steps(1) infinite`,
});

export const getGreetingSubtitleStyle = (palette: Palette) => ({
  color: palette.text.label,
  fontSize: "14px",
  maxWidth: 450,
  lineHeight: 1.5,
});

export const getOptionRowStyle = (palette: Palette, isSelected: boolean) => ({
  p: 1.5,
  borderRadius: 1.5,
  cursor: "pointer",
  backgroundColor: isSelected ? palette.text.input : "transparent",
  "&:hover": { backgroundColor: palette.text.input },
});

export const optionTitleStyle = (palette: Palette) => ({
  fontWeight: 600,
  color: palette.text.heading,
  fontSize: "14px",
});

export const optionDescriptionStyle = (palette: Palette) => ({
  display: "block",
  mt: 0.5,
  color: palette.text.label,
  fontSize: "12px",
  fontWeight: 400,
});

export const modelMenuPopoverStyle = { width: 272, p: 1 };

export const preferencesMenuPopoverStyle = {
  width: { xs: "90vw", sm: 480 },
  maxWidth: 480,
  height: 340,
  p: 0,
  display: "flex",
};

export const preferencesSidebarStyle = {
  width: 140,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRight: "1px solid",
  borderColor: "text.borderLine",
  p: 1,
};

export const preferencesSidebarFooterStyle = (palette: Palette) => ({
  px: 1.5,
  pb: 1,
  color: palette.text.disabled,
  fontSize: "12px",
  fontWeight: 500,
});

export const getTabButtonStyle = (palette: Palette, isActive: boolean) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  px: 1.5,
  py: 1,
  borderRadius: 1,
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 500,
  color: palette.primary.main,
  backgroundColor: isActive ? palette.text.input : "transparent",
  "&:hover": { backgroundColor: palette.text.input },
});

export const sectionLabelStyle = (palette: Palette) => ({
  fontWeight: 600,
  fontSize: "12px",
  letterSpacing: "normal",
  color: palette.primary.main,
  pt: 1,
  pb: 1,
  textTransform: "none" as const,
});

export const sectionDividerStyle = {
  borderBottom: "1px solid",
  borderColor: "text.borderLine",
  mb: 1,
};

export const promptCategoriesWrapperStyle = {
  flexWrap: "wrap" as const,
  rowGap: 0.5,
  px: 1.5,
  pb: 1,
};

export const emptyStateTextStyle = (palette: Palette) => ({
  display: "block",
  px: 1,
  pb: 1,
  color: palette.text.label,
  fontSize: "12px",
  fontWeight: 400,
});

export const promptRowStyle = (palette: Palette) => ({
  px: 1,
  py: 1,
  borderRadius: 1,
  cursor: "pointer",
  "&:hover": { backgroundColor: palette.text.input },
});

export const promptRowTextStyle = (palette: Palette) => ({
  flex: 1,
  minWidth: 0,
  fontSize: "12px",
  color: palette.text.primary,
  fontWeight: 400,
});

export const getRowDeleteIconButtonStyle = (palette: Palette) => ({
  p: 0.5,
  flexShrink: 0,
  color: palette.text.disabled,
  "&:hover": { color: palette.error.main, backgroundColor: "transparent" },
});

export const clearAllTextStyle = (palette: Palette) => ({
  cursor: "pointer",
  color: palette.text.label,
  fontSize: "12px",
  fontWeight: 400,
  "&:hover": { color: palette.error.main },
});

export const memoryRowStyle = (palette: Palette) => ({
  px: 1,
  py: 1,
  borderRadius: 1,
  "&:hover": { backgroundColor: palette.text.input },
});

export const memoryTextStyle = (palette: Palette) => ({
  fontSize: "12px",
  lineHeight: 1.5,
  color: palette.text.primary,
  fontWeight: 400,
});

export const memoryWhenStyle = (palette: Palette) => ({
  display: "block",
  mt: 0.5,
  color: palette.text.label,
  fontSize: "12px",
  fontWeight: 400,
});

export const emptyMemoriesTextStyle = (palette: Palette) => ({
  display: "block",
  px: 1,
  py: 2,
  color: palette.text.label,
  fontSize: "12px",
  lineHeight: 1.5,
  fontWeight: 400,
});

export const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const getSyncRowVisual = (
  status: import("../hooks/useSyncProgress").ISyncProgressRow["status"],
  palette: Palette,
) => {
  switch (status) {
    case "error":
      return {
        border: palette.error.lighter,
        bg: palette.background.paper,
        color: palette.error.main,
      };
    case "busy":
      return {
        border: palette.primary.main,
        bg: palette.background.paper,
        color: palette.primary.main,
      };
    case "done":
      return {
        border: palette.text.borderLine,
        bg: palette.background.paper,
        color: palette.text.heading,
      };
    default:
      return {
        border: palette.text.borderLine,
        bg: palette.background.paper,
        color: palette.text.label,
      };
  }
};

export const syncOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1301,
  backgroundColor: "rgba(0,29,63,0.36)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: 3,
};

export const getSyncModalBoxStyle = (palette: Palette) => ({
  width: "100%",
  maxWidth: 480,
  backgroundColor: palette.background.paper,
  borderRadius: 2,
  boxShadow: "0 24px 60px rgba(0,29,63,0.24)",
  overflow: "hidden",
});

export const syncHeaderStackStyle = {
  p: 2,
  borderBottom: "1px solid",
  borderColor: "text.borderLine",
};

export const getSyncHeaderTitleStyle = (palette: Palette) => ({
  fontWeight: 700,
  color: palette.text.heading,
});

export const getSyncHeaderSubtitleStyle = (palette: Palette) => ({
  display: "block",
  mt: 0.5,
  color: palette.text.label,
  fontSize: "12px",
});

export const syncRowsStackStyle = { p: 2 };

export const getSyncRowBoxStyle = (visual: { border: string; bg: string }) => ({
  border: `1px solid ${visual.border}`,
  borderRadius: 1.5,
  backgroundColor: visual.bg,
  p: 1.5,
  transition: "border-color 200ms, background-color 200ms",
});

export const syncRowStatusIconWrapperStyle = {
  width: 18,
  height: 18,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const getSyncPendingDotStyle = (palette: Palette) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  border: `2px solid ${palette.text.borderLine}`,
});

export const getSyncSpinningIconStyle = (palette: Palette) => ({
  color: palette.primary.main,
  animation: `${spinAnimation} 800ms linear infinite`,
});

export const syncDoneCircleStyle = {
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: "success.lighter",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const syncErrorCircleStyle = {
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: "error.lighter",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const getSyncRowLabelStyle = (visual: { color: string }) => ({
  flex: 1,
  minWidth: 0,
  fontWeight: 600,
  fontSize: "14px",
  color: visual.color,
});

export const getSyncRowErrorMessageStyle = (palette: Palette) => ({
  display: "block",
  mt: 1,
  pl: "30px",
  color: palette.error.main,
  fontSize: "12px",
});

export const syncFooterStackStyle = (palette: Palette) => ({
  p: 2,
  borderTop: "1px solid",
  borderColor: "text.borderLine",
  backgroundColor: palette.background.default,
});

export const getSyncRetryButtonStyle = (palette: Palette) => ({
  mr: "auto",
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 2,
  py: 1,
  borderRadius: 1.5,
  border: `1px solid ${palette.primary.main}`,
  color: palette.primary.main,
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  "&:hover": { backgroundColor: palette.text.input },
});

export const getSyncDoneButtonStyle = (
  palette: Palette,
  isRunning: boolean,
) => ({
  px: 2,
  py: 1,
  borderRadius: 1.5,
  backgroundColor: isRunning ? "text.disabled" : palette.text.heading,
  color: palette.common.white,
  fontSize: "14px",
  fontWeight: 600,
  cursor: isRunning ? "default" : "pointer",
});

export const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export const shimmerAnimation = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

export const toolBlockContainerStyle = { p: 1.5, mb: 1 };

export const toolBlockHeaderStyle = {
  ...generalStyles.flexWithGapStyle,
  cursor: "pointer",
};

export const spinningLoaderIconStyle = {
  animation: `${spinAnimation} 1s linear infinite`,
  display: "inline-flex",
};

export const getToolStatusLabelStyle = (
  palette: Palette,
  isActive: boolean,
) => ({
  color: palette.text.label,
  animation: isActive ? `${pulseAnimation} 1.5s ease-in-out infinite` : "none",
  display: "flex",
  alignItems: "center",
});

export const getToolSummaryTextStyle = (palette: Palette) => ({
  whiteSpace: "pre-wrap" as const,
  mt: 1,
  display: "block",
  color: palette.text.secondary,
});

export const getThinkingStepActiveDotStyle = (palette: Palette) => ({
  width: 12,
  height: 12,
  mt: "4px",
  flexShrink: 0,
  borderRadius: "50%",
  border: `2px solid ${palette.secondary.dark}`,
  borderTopColor: "transparent",
  animation: `${spinAnimation} 800ms linear infinite`,
});

export const thinkingStepInactiveDotStyle = {
  width: 12,
  height: 12,
  mt: "4px",
  flexShrink: 0,
};

export const getThinkingStepActiveLabelStyle = (palette: Palette) => ({
  fontSize: "14px",
  fontWeight: 500,
  backgroundImage: `linear-gradient(90deg, ${palette.text.label} 20%, ${palette.text.heading} 45%, ${palette.text.label} 70%)`,
  backgroundSize: "200% 100%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
  animation: `${shimmerAnimation} 1.5s linear infinite`,
});

export const getThinkingStepLabelStyle = (palette: Palette, done: boolean) => ({
  fontSize: "14px",
  color: done ? palette.text.label : palette.text.secondary,
});

export const getThinkingBlockContainerStyle = (palette: Palette) => ({
  ...borderGeneralStyle,
  borderRadius: 1.5,
  overflow: "hidden",
  mb: 1,
  backgroundColor:
    palette.mode === "dark" ? palette.grey[900] : palette.background.default,
});

export const thinkingBlockHeaderStyle = {
  ...generalStyles.flexWithGapStyle,
  cursor: "pointer",
  p: 1.5,
};

export const getThinkingBlockTitleStyle = (palette: Palette) => ({
  color: palette.text.label,
  display: "flex",
  alignItems: "center",
  flex: 1,
});

export const thinkingBlockBodyStyle = { px: 1.5, pb: 1.5 };

export const getThinkingContentTextStyle = (palette: Palette) => ({
  whiteSpace: "pre-wrap" as const,
  mb: 1,
  display: "block",
  color: palette.text.label,
});

export const getDocPreviewDialogPaperStyle = (hasCsvData: boolean) => ({
  borderRadius: 4,
  height: hasCsvData ? "auto" : "85vh",
  maxHeight: "90vh",
  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
  backgroundImage: "none",
});

export const docPreviewDialogTitleStyle = {
  m: 0,
  px: 2,
  py: 2,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const getDocPreviewTitleTextStyle = (palette: Palette) => ({
  flexGrow: 1,
  fontWeight: 600,
  color: palette.text.heading,
  fontSize: "14px",
});

export const getDocPreviewCloseButtonStyle = (palette: Palette) => ({
  color: palette.text.label,
  "&:hover": { backgroundColor: palette.action.hover },
});

export const getDocPreviewDialogContentStyle = (
  palette: Palette,
  fileType: "pdf" | "image" | "other" | null,
) => ({
  p: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  backgroundColor:
    fileType === "image"
      ? palette.mode === "dark"
        ? "#000"
        : "#f5f5f5"
      : "transparent",
});

export const docPreviewLoadingStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  p: 4,
};

export const docPreviewErrorStyle = {
  height: "100%",
  p: 4,
  textAlign: "center" as const,
};

export const docPreviewImageWrapperStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: 2,
  overflow: "auto",
};

export const docPreviewImageStyle = {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain" as const,
};

export const docPreviewIframeStyle = { border: "none", flexGrow: 1 };

export const docPreviewTableWrapperStyle = {
  overflow: "auto",
  width: "100%",
  height: "100%",
};

export const getDocPreviewTableStyle = (
  palette: Palette,
  isExcel: boolean,
) => ({
  minWidth: 600,
  borderCollapse: "collapse" as const,
  border: isExcel ? `1px solid ${palette.divider}` : "none",
  p: 2,
});

export const getDocPreviewHeaderCellStyle = (palette: Palette) => ({
  fontWeight: 600,
  fontSize: "12px",
  color: palette.text.heading,
  backgroundColor:
    palette.mode === "dark" ? palette.background.default : "#F8FAFC",
  borderBottom: `1px solid ${palette.divider}`,
  py: 1.5,
  px: 2,
});

export const getDocPreviewSheetHeaderCellStyle = (palette: Palette) => ({
  backgroundColor: palette.info?.lighter || "#F0F7FF",
  color: palette.info?.dark || "#0052CC",
  fontWeight: 700,
  fontSize: "12px",
  textTransform: "uppercase" as const,
  py: 1.5,
  px: 2,
  border: `1px solid ${palette.divider}`,
});

export const getDocPreviewRowStyle = (palette: Palette, isExcel: boolean) => ({
  "&:last-child td, &:last-child th": {
    border: isExcel ? undefined : 0,
  },
  "&:hover": { backgroundColor: palette.action.hover },
});

export const getDocPreviewCellStyle = (palette: Palette, isExcel: boolean) => ({
  color: palette.text.primary,
  fontSize: "14px",
  border: isExcel ? `1px solid ${palette.divider}` : "none",
  borderBottom: `1px solid ${palette.divider}`,
  py: 1,
  px: 2,
});
